import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { versionLabel, type MyPolicies } from '@grc/types';
import { apiFetch } from '../../lib/api.js';
import { useOrg } from '../../lib/auth.js';
import { Badge, EmptyState, LoadingPane, PageHeader, Table, formatDate } from '../../components/ui.js';
import { STATUS_META, myPoliciesKey, reasonLabel } from './shared.js';
import './policies.css';

/** Every policy published for acceptance, with where the caller stands. */
export function MyPoliciesPage() {
  const { org } = useOrg();
  const policies = useQuery({
    queryKey: myPoliciesKey(org.orgId),
    queryFn: () => apiFetch<MyPolicies>(`/orgs/${org.orgId}/my-policies`),
  });

  if (policies.isLoading) return <LoadingPane label="Loading your policies" />;
  if (policies.isError || !policies.data) {
    return (
      <div>
        <PageHeader title="My policies" />
        <EmptyState
          title="Could not load your policies"
          message="Something went wrong fetching the published policies — please try again shortly."
        />
      </div>
    );
  }
  const { items, counts } = policies.data;
  const open = counts.outstanding + counts.overdue;

  return (
    <div>
      <PageHeader
        title="My policies"
        subtitle={
          items.length === 0
            ? 'Policies your organisation publishes for acceptance appear here.'
            : open === 0
              ? `You have accepted all ${items.length} published ${items.length === 1 ? 'policy' : 'policies'}.`
              : `${open} ${open === 1 ? 'policy needs' : 'policies need'} your acceptance${
                  counts.overdue > 0 ? ` · ${counts.overdue} overdue` : ''
                }.`
        }
      />
      {items.length === 0 ? (
        <EmptyState
          title="Nothing to accept yet"
          message="When an admin publishes a policy for acceptance it will be listed here with its due date."
        />
      ) : (
        <Table
          head={
            <>
              <th>Policy</th>
              <th>Version</th>
              <th>Status</th>
              <th>Date</th>
            </>
          }
        >
          {items.map((item) => {
            const meta = STATUS_META[item.status.status];
            const sub = reasonLabel(item.status.reason, item.reacceptIntervalMonths);
            return (
              <tr key={item.releaseId}>
                <td>
                  <Link to={`/policies/${item.releaseId}`} className="policy-row-title">
                    {item.title}
                  </Link>
                  <div className="policy-row-key">{item.key}</div>
                </td>
                <td>{versionLabel(item.documentVersion, item.versionNumber)}</td>
                <td>
                  <Badge tone={meta.tone}>{meta.label}</Badge>
                  {sub && <span className="policy-status-sub">{sub}</span>}
                </td>
                <td style={{ whiteSpace: 'nowrap' }}>
                  {item.status.status === 'accepted' ? (
                    <>
                      Accepted {formatDate(item.status.acceptedAt)}
                      {item.status.renewBy && (
                        <span className="policy-status-sub">Reconfirm by {formatDate(item.status.renewBy)}</span>
                      )}
                    </>
                  ) : (
                    <>Due {formatDate(item.status.dueOn)}</>
                  )}
                </td>
              </tr>
            );
          })}
        </Table>
      )}
    </div>
  );
}
